// Rotating offer strip for the revamp PDP hero. Every slide is rendered
// server-side; this element only moves `is-active` / `inert` between them.
//
// There is no timer: the active progress bar's CSS fill animation is the clock,
// and `animationend` advances the strip. Pausing that animation (hover, focus,
// off-screen, editor selection) therefore pauses rotation for free, and
// reduced-motion users — whose stylesheet drops the animation — get a static
// strip they step through with the bars.
(() => {
  const SLIDE_SELECTOR = '[data-hira-promo-slide]';
  const BAR_SELECTOR = '[data-hira-promo-bar]';
  const SWIPE_THRESHOLD_PX = 40;

  class HiraPromoStrip extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.slides = [...this.querySelectorAll(SLIDE_SELECTOR)];
      this.bars = [...this.querySelectorAll(BAR_SELECTOR)];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));

      if (this.slides.length < 2) return;

      this.addEventListener('click', this.handleClick);
      this.addEventListener('animationend', this.handleAnimationEnd);
      this.addEventListener('pointerdown', this.handlePointerDown);
      this.addEventListener('pointerup', this.handlePointerUp);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

      // Off-screen strips hold still, so a shopper scrolling back up lands on
      // the slide they left rather than one they never saw.
      this.observer = new IntersectionObserver(([entry]) => {
        this.toggleAttribute('data-hira-promo-offscreen', !entry.isIntersecting);
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('animationend', this.handleAnimationEnd);
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.removeEventListener('pointerup', this.handlePointerUp);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      this.observer?.disconnect();
    }

    show(nextIndex) {
      const count = this.slides.length;
      const index = ((nextIndex % count) + count) % count;

      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.inert = !isActive;
      });

      // Bars before the active one stay full; the active one restarts its
      // fill. Removing and re-adding the class on the same frame would not
      // restart a CSS animation, hence the forced reflow.
      this.bars.forEach((bar, i) => {
        bar.classList.remove('is-active');
        bar.classList.toggle('is-done', i < index);
        bar.toggleAttribute('aria-current', i === index);
      });
      const activeBar = this.bars[index];
      if (activeBar) {
        void activeBar.offsetWidth;
        activeBar.classList.add('is-active');
      }

      this.index = index;
    }

    handleClick({ target }) {
      const bar = target.closest(BAR_SELECTOR);
      if (!bar) return;

      this.show(this.bars.indexOf(bar));
    }

    // Only the active bar's fill animates, so any `animationend` that bubbles
    // up from it means the current slide's time is up.
    handleAnimationEnd({ target }) {
      if (!target.closest(`${BAR_SELECTOR}.is-active`)) return;

      this.show(this.index + 1);
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse') return;

      this.swipeStartX = event.clientX;
    }

    handlePointerUp(event) {
      if (this.swipeStartX === undefined) return;

      const deltaX = event.clientX - this.swipeStartX;
      this.swipeStartX = undefined;

      if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;

      this.show(this.index + (deltaX < 0 ? 1 : -1));
    }

    // Selecting a slide in the editor sidebar jumps to it and holds it there
    // until deselected, so the merchant is not chasing a moving target.
    handleBlockSelect({ target }) {
      const slideIndex = this.slides.indexOf(target);
      if (slideIndex === -1) return;

      this.show(slideIndex);
      this.setAttribute('data-hira-promo-held', '');
    }

    handleBlockDeselect({ target }) {
      if (!this.slides.includes(target)) return;

      this.removeAttribute('data-hira-promo-held');
    }
  }

  if (!customElements.get('hira-promo-strip')) {
    customElements.define('hira-promo-strip', HiraPromoStrip);
  }
})();
